"use client"

/**
 * Six mistakes that sink a seeding job. Sits below the prep checklist on
 * /tools/seeding-calculator. Copy from the Claude Design polish.
 */

const S_INK = "#111827"
const S_INK2 = "#374151"
const S_RULE = "#e5e7eb"
const S_BRAND_700 = "#15803d"
const S_DANGER = "#dc2626"
const S_DANGER_50 = "#fef2f2"

interface Mistake {
  title: string
  body: string
}

const MISTAKES: Mistake[] = [
  {
    title: "Seeding too early in fall",
    body:
      "Summer heat fries seedlings. Wait for daytime highs in the 70s and consistent overnight cooling — that’s the real signal, not a calendar date.",
  },
  {
    title: "Seeding too late",
    body:
      "New grass needs about six weeks of mild weather to root before frost. Past the cutoff you’re feeding birds. The window above bakes in your zone’s frost dates.",
  },
  {
    title: "Skipping the rake",
    body:
      "Seed on top of thatch or compacted soil is seed in the wrong place. Scratch the surface first so it can reach bare dirt, no matter how the lawn looks.",
  },
  {
    title: "Letting it dry out",
    body:
      "A single hot afternoon can kill a seeding once the seed has cracked. Water lightly twice a day for the first two weeks, then taper as it establishes.",
  },
  {
    title: "Mowing too soon",
    body:
      "New grass needs to hit 3–4 inches before its first cut, and even then only the top third. Cutting earlier rips young roots out of the soil.",
  },
  {
    title: "Pre-emergent + seed at the same time",
    body:
      "Crabgrass preventer prevents your seed too. Skip the spring pre-emergent in any area you’re overseeding — or use one of the few seed-safe formulas (mesotrione).",
  },
]

export function SeedingMistakes() {
  return (
    <section
      aria-labelledby="seeding-mistakes-heading"
      style={{ marginTop: 40, color: S_INK }}
    >
      <div style={{ marginBottom: 20, maxWidth: 720 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: S_BRAND_700,
            marginBottom: 8,
          }}
        >
          Easy to get wrong
        </div>
        <h2
          id="seeding-mistakes-heading"
          style={{
            margin: 0,
            fontSize: 26,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
            color: S_INK,
          }}
        >
          Six mistakes that kill a seeding
        </h2>
        <p style={{ margin: "8px 0 0", fontSize: 15, color: S_INK2, lineHeight: 1.6 }}>
          The window is the easy part. These are the things people get wrong inside the
          window.
        </p>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 14,
        }}
      >
        {MISTAKES.map((m) => (
          <div
            key={m.title}
            style={{
              background: "#fff",
              border: `1px solid ${S_RULE}`,
              borderRadius: 14,
              padding: "18px 20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 11,
                  background: S_DANGER_50,
                  color: S_DANGER,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  marginTop: 1,
                }}
                aria-hidden="true"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </span>
              <div style={{ fontSize: 15, fontWeight: 600, color: S_INK, lineHeight: 1.35 }}>
                {m.title}
              </div>
            </div>
            <div style={{ fontSize: 13.5, color: S_INK2, lineHeight: 1.6 }}>{m.body}</div>
          </div>
        ))}
      </div>
    </section>
  )
}
